import { COLOR_PALETTE } from 'constants/ui';
import { getConditionalObject } from 'utils/data';

// Purple "Needed" token reused from the field-level indicator (DEV-4383) so the
// roll-up reads as the same signal. The deep PURPLE[7] is for a LIGHT backdrop
// (light theme, and the always-light inner-nav row); on a DARK backdrop (dark
// theme's rail + the dark-theme inner-nav row) it lifts to a lighter purple so
// the dot/text/accent stay legible.
export const getNeededPurple = palette =>
  palette.mode === 'dark' ? COLOR_PALETTE.PURPLE[4] : COLOR_PALETTE.PURPLE[7];

// The inner-nav highlighted-row tint. It MUST flip with the theme in lockstep
// with `getNeededPurple`: a light tint in light mode (the approved Style C
// lavender) and a deep tint in dark mode — otherwise the theme-aware light pill
// would land on a near-white row and wash out (DEV-4496 dark-mode contrast fix).
export const getNeededRowBackground = palette =>
  palette.mode === 'dark' ? COLOR_PALETTE.PURPLE[10] : COLOR_PALETTE.PURPLE[1];

export const indicatorStyles = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  flexShrink: 0,
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: '18px',
  color: ({ palette }) => getNeededPurple(palette),
  cursor: 'default',
};

export const dotStyles = {
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: ({ palette }) => getNeededPurple(palette),
  flexShrink: 0,
};

// Style C "highlighted row" for the inner section nav — purple tint + a purple
// left accent bar, both theme-aware so the row + pill stay legible in dark mode.
// Re-applied under `aria-selected` so a selected-AND-needed row keeps the purple
// tint rather than falling back to the neutral selected fill.
export const navNeededRowStyles = {
  backgroundColor: ({ palette }) => getNeededRowBackground(palette),
  borderLeftWidth: '2px',
  borderLeftStyle: 'solid',
  borderLeftColor: ({ palette }) => getNeededPurple(palette),
  '&[aria-selected="true"]': {
    backgroundColor: ({ palette }) => getNeededRowBackground(palette),
  },
};

// Outer-rail left accent bar — the dark rail keeps its dark fill (a light tint
// would flash near-white on it), so the roll-up signal there is the accent bar
// plus the indicator pill rather than a tinted row.
export const getRailAccentStyles = ({ isHidden = false } = {}) => ({
  position: 'absolute',
  width: '4px',
  height: '100%',
  left: 0,
  top: 0,
  borderTopLeftRadius: '8px',
  borderBottomLeftRadius: '8px',
  backgroundColor: ({ palette }) => getNeededPurple(palette),
  ...getConditionalObject(isHidden, { display: 'none' }),
});
